<script setup lang="ts">
import type { NovaKycContentPendingProps } from './NovaKycContentPending.types'
import { NovaModalCommon, NovaModalReportCustomer } from '#components'

defineProps<NovaKycContentPendingProps>()
const { show: modalShow, hide: modalHide } = useModal()
const { t } = useI18n()
const { gtEvent } = useGoogleTag()
const handleCsClick = computed(() => {
  modalShow({
    component: NovaModalCommon,
    bind: {
      name: modalsName.MODAL_CS,
      showCloseBtn: true,
    },
    on: {
      close: async () => {
        gtEvent('clickEvent', {
          eventCategory: '클릭',
          eventAction: '고객 문의 모달 닫기',
          eventLabel: t('cs'),
          eventSlot: '유저 패널',
          eventI18nAddr: useKoreanTranslation('cs'),
          eventComponent: 'Button',
        })
        await modalHide(modalsName.MODAL_CS)
      },
    },
    slots: {
      title: t('qna.title'),
      contents: {
        component: NovaModalReportCustomer,
      },
    },
  })
})
</script>

<template>
  <div class="guide">
    <div class="top-txt-box">
      <div
        v-dompurify-html="t(`kyc.unAuthKyc.pending.primaryCopy`)"
        class="primary-copy"
      />

      <div
        v-dompurify-html="t(`kyc.unAuthKyc.pending.secondaryCopy`)"
        class="secondary-copy"
      />
    </div>
    <div class="step-item">
      <ul>
        <li class="step-copy one">
          <p class="icon-box">
            <span class="icon" />
          </p>
          <p
            v-dompurify-html="t(`kyc.unAuthKyc.pending.stepCopy.step1`)"
            class="txt"
          />
        </li>
        <li class="step-copy two">
          <p class="icon-box">
            <span class="icon">
              <NovaIcon
                :icon="{ type: 'outline', name: 'check2' }"
                :size="16"
              />
            </span>
          </p>
          <p
            v-dompurify-html="t(`kyc.unAuthKyc.pending.stepCopy.step2`)"
            class="txt"
          />
        </li>
        <li class="step-copy three">
          <p class="icon-box">
            <span class="icon" />
          </p>
          <p
            v-dompurify-html="t(`kyc.unAuthKyc.pending.stepCopy.step3`)"
            class="txt"
          />
        </li>
      </ul>
    </div>

    <div
      v-dompurify-html="t(`kyc.unAuthKyc.pending.cs`)"
      class="description"
      @click="handleCsClick"
    />
  </div>
</template>

<style lang="scss" scoped>
.guide {
  display: flex;
  flex-direction: column;
  gap: 12px;
  .top-txt-box {
    padding-bottom: 16px;
    border-bottom: 1px solid $color-gray-400;
    .primary-copy {
      @include text-style($text-custom-18-bold);
      color: $color-black;
      text-align: center;
      line-height: 1.2;
    }
    .secondary-copy {
      padding-top: 12px;
      @include text-style($text-display-regular);
      color: $color-text-12;
      text-align: center;
      line-height: 1.2;
      letter-spacing: -0.8px;
    }
  }
  .guide-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px;
    border-radius: 6px;
    background-color: $color-gray-1;
    @include text-style($text-body-12-regular);
    color: $color-text-2;
  }

  .step-item {
    padding-top: 20px;
    padding-bottom: 20px;

    ul {
      position: relative;
      display: flex;
      justify-content: space-between;
      flex-direction: row;
      align-items: center;
      &:before {
        position: absolute;
        top: 15px;
        left: 50%;
        transform: translateX(-50%);
        display: block;
        content: '';
        width: 70%;
        height: 2px;
        background: linear-gradient(
          90deg,
          $color-sky-blue 50%,
          $color-gray-200 50%
        );
        z-index: 0;
      }
      li {
        position: relative;
        display: flex;
        justify-content: center;
        flex-direction: column;
        gap: 4px;
        text-align: center;
        width: calc(100% / 3);
        z-index: 1;
        .icon-box {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 32px;
          height: 32px;
          margin: 0 auto;
          .icon {
            content: '';
            display: block;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            margin: 0 auto;
          }
        }
        .txt {
          min-height: 44px;
          color: $color-sky-blue;
          @include text-style($text-body-14-bold);
        }
        &.one {
          .icon-box {
            .icon {
              background-color: $color-white;
              border-width: 3px;
              border-style: solid;
              border-color: $color-sky-blue;
            }
          }
          .txt {
            color: $color-sky-blue;
            @include text-style($text-body-14-bold);
            line-height: 1.2;
          }
        }
        &.two {
          .icon-box {
            .icon {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 30px;
              height: 30px;
              background-color: $color-sky-blue;
              color: #ffffff;
            }
          }

          .txt {
            color: $color-sky-blue;
            @include text-style($text-body-14-bold);
            line-height: 1.2;
          }
        }

        &.three {
          .icon-box {
            .icon {
              background-color: $color-gray-200;
              border-width: 3px;
              border-style: solid;
              border-color: $color-gray-200;
            }
          }

          .txt {
            color: $color-text-12;
            @include text-style($text-body-14-medium);
            line-height: 1.2;
          }
        }
      }
    }
  }

  .description {
    @include text-style($text-body-14-regular);
    text-align: left;
    line-height: 1.2;
    color: $color-gray-500;
    padding-left: 4px;
    text-indent: -4px;
    word-break: break-all;
  }
}
</style>
